import { FC, useEffect } from 'react'

import { ArrowLeftIcon } from '@inno-ui/icons'
import clsx from 'clsx'
import { AuthButton } from 'modules/auth/components'
import {
  AuthButtonColor,
  AuthButtonVariant
} from 'modules/auth/components/AuthButton/AuthButton.constant'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PublicFooter } from 'shared/components'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { useHistory } from 'shared/hooks/useHistory'
import { useUserContext } from 'shared/hooks/useUserContext'
import { isUserAnonymous } from 'shared/utils/isUserAnonymous'
import { lsGetItem, lsRemoveItem } from 'shared/utils/local-storage'

import { AuthLanguageSelector } from './AuthLanguageSelector'
import styles from './AuthPageTemplate.module.scss'
import { AuthPageTemplateProps } from './AuthPageTemplate.types'
import { AuthTemplateHeader } from './AuthTemplateHeader'

export const AuthPageTemplate: FC<AuthPageTemplateProps> = ({
  children,
  className,
  backgroundImageUrl,
  backButton,
  showHeader = true,
  showHeaderButtons = true
}) => {
  const navigate = useNavigate()
  const history = useHistory()
  const selectableUsers = lsGetItem(LocalStorageKey.SELECTABLE_USERS) || []
  const { t } = useTranslation('auth')

  const { user } = useUserContext()

  const hasHistory = history.length > 1
  const hasCustomBackButtonAction =
    backButton instanceof Object && 'onClick' in backButton
  const showBackButton =
    hasCustomBackButtonAction || (hasHistory && backButton === true)

  useEffect(() => {
    if (!!user && isUserAnonymous(user)) {
      lsRemoveItem(LocalStorageKey.USER)
      window.location.reload()
    }
  }, [user])

  const defaultBackButtonAction = () => {
    navigate(-1)
  }

  return (
    <motion.div
      className={styles.authPage}
      style={{
        backgroundImage: backgroundImageUrl
          ? `url(${backgroundImageUrl})`
          : undefined
      }}
    >
      {showHeader && (
        <AuthTemplateHeader
          showHeaderButtons={showHeaderButtons}
          hasSelectableUsers={selectableUsers.length > 0}
        />
      )}
      <div className={clsx(styles.content, className)}>
        <div className={styles.contentInner}>
          {children}
          {showBackButton && (
            <div className={styles.backButton}>
              <AuthButton
                icon={ArrowLeftIcon}
                colorType={AuthButtonColor.DARK}
                onClick={
                  hasCustomBackButtonAction
                    ? backButton.onClick
                    : defaultBackButtonAction
                }
                variant={AuthButtonVariant.GHOST}
                isBold
                label={t('back_button')}
              />
            </div>
          )}
        </div>
      </div>
      <AuthLanguageSelector />
      <PublicFooter />
    </motion.div>
  )
}
